import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MemberLayout from "../components/MemberLayout";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Loader2, Mic, MicOff, Send } from "lucide-react";
import { toast } from "sonner";
import ScenarioErrorBoundary from "../components/ScenarioErrorBoundary";

const ZONE_META = {
  resilience: { label: "Resilience Zone", color: "hsl(var(--haruki-resilience))" },
  neutral:    { label: "Neutral Zone",    color: "hsl(var(--haruki-neutral))" },
  fire:       { label: "Emotional Fire Zone", color: "hsl(var(--haruki-fire))" },
};

export default function ScenarioPage() {
  return (
    <ScenarioErrorBoundary>
      <Scenario />
    </ScenarioErrorBoundary>
  );
}

function Scenario() {
  const { moduleType } = useParams();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stageMsg, setStageMsg] = useState("Choosing your scenario…");

  // Staged, honest loading messages for cold starts (buffered loads finish
  // before the second message ever shows).
  useEffect(() => {
    if (!loading) return;
    const STAGES = [
      [0, "Choosing your scenario…"],
      [4, "Writing a scenario for your role…"],
      [14, "Running the clinical safety check…"],
      [30, "Almost there — finalizing your scenario…"],
    ];
    setStageMsg(STAGES[0][1]);
    const started = Date.now();
    const iv = setInterval(() => {
      const s = (Date.now() - started) / 1000;
      const stage = [...STAGES].reverse().find(([t]) => s >= t);
      if (stage) setStageMsg(stage[1]);
    }, 1000);
    return () => clearInterval(iv);
  }, [loading]);
  const [scenario, setScenario] = useState(null);
  const [source, setSource] = useState(null);
  const [voiceText, setVoiceText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [feedback, setFeedback] = useState(null); // { zone, reflection_prompt, module_complete }
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const nextRef = useRef(null); // prefetched /scenarios/next promise

  const loadError = (e) => {
    if (e?.response?.data?.detail?.code === "upgrade_required") {
      toast.error("Your trial has ended. Upgrade to continue training.");
    } else {
      toast.error("Could not load scenario.");
    }
    nav("/app");
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.post("/scenarios/next", { module_type: moduleType });
        setScenario(r.data.scenario);
        setSource(r.data.source);
      } catch (e) {
        loadError(e);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleType]);

  // Prefetch the next scenario while the user reads their reflection —
  // hides the ~10s runtime-generation latency entirely.
  const prefetchNext = () => {
    nextRef.current = api
      .post("/scenarios/next", { module_type: moduleType })
      .catch(() => null);
  };

  const goNext = async () => {
    setFeedback(null);
    setScenario(null);
    setVoiceText("");
    setLoading(true); // never render the player with scenario=null
    try {
      let r = null;
      if (nextRef.current) {
        r = await nextRef.current;
        nextRef.current = null;
      }
      if (!r) r = await api.post("/scenarios/next", { module_type: moduleType });
      setScenario(r.data.scenario);
      setSource(r.data.source);
    } catch (e) {
      loadError(e);
    } finally {
      setLoading(false);
    }
  };

  const submitChoice = async (option) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const r = await api.post("/scenarios/respond", {
        scenario_id: scenario.scenario_id,
        response_text: option.text,
        response_method: "choice",
        module_type: moduleType,
        runtime_context: source === "runtime" ? scenario : undefined,
      });
      setFeedback(r.data);
      if (!r.data.module_complete) prefetchNext();
    } catch (e) {
      toast.error("Could not submit response.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitVoice = async () => {
    if (!voiceText.trim()) { toast.error("Speak or type a response first."); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/scenarios/respond", {
        scenario_id: scenario.scenario_id,
        response_text: voiceText,
        response_method: "voice",
        module_type: moduleType,
        runtime_context: source === "runtime" ? scenario : undefined,
      });
      setFeedback(r.data);
      if (!r.data.module_complete) prefetchNext();
    } catch (e) {
      toast.error("Could not submit response.");
    } finally {
      setSubmitting(false);
    }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => chunksRef.current.push(e.data);
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const fd = new FormData();
        fd.append("audio", blob, "response.webm");
        try {
          const r = await api.post("/scenarios/voice-transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setVoiceText(r.data.text || "");
        } catch (e) {
          toast.error("Transcription failed.");
        }
        stream.getTracks().forEach(t => t.stop());
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (e) {
      toast.error("Microphone access denied.");
    }
  };

  const stopRec = () => {
    if (recorderRef.current && recording) {
      recorderRef.current.stop();
      setRecording(false);
    }
  };

  // Skeleton renders IMMEDIATELY on load or transition — the player below is
  // never reached with scenario=null (root cause of the white-screen crash).
  if (loading || (!scenario && !feedback)) {
    return (
      <MemberLayout>
        <div className="haruki-fade-in space-y-6" data-testid="scenario-skeleton">
          <div className="h-3 w-48 animate-pulse rounded bg-muted" />
          <div className="aspect-video w-full animate-pulse rounded-md bg-muted" />
          <div className="space-y-2 rounded-md border border-border p-6">
            <div className="h-4 w-full animate-pulse rounded bg-muted" />
            <div className="h-4 w-5/6 animate-pulse rounded bg-muted" />
            <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
          </div>
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-12 w-full animate-pulse rounded-md bg-muted" />
            ))}
          </div>
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span data-testid="scenario-loading-stage">{stageMsg}</span>
          </div>
        </div>
      </MemberLayout>
    );
  }

  if (feedback) {
    const meta = ZONE_META[feedback.zone] || ZONE_META.neutral;
    return (
      <MemberLayout>
        <div className="haruki-fade-in space-y-8">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Reflection
          </div>
          {feedback.hmde_fallback ? (
            <div
              data-testid="hmde-fallback-notice"
              className="rounded-lg border p-6"
              style={{ borderColor: "hsl(var(--haruki-rust, 20 60% 50%))" }}
            >
              <div className="mb-1 text-xs uppercase tracking-widest text-muted-foreground">Feedback</div>
              <div className="font-heading text-lg font-semibold">
                Automated feedback is temporarily unavailable
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                Your response was saved and counts toward your progress. It just couldn't
                be scored right now — this doesn't reflect on your answer.
              </p>
            </div>
          ) : (
            <div
              data-testid="hmde-zone-label"
              className="rounded-lg border p-6"
              style={{ borderColor: meta.color }}
            >
              <div className="mb-1 text-xs uppercase tracking-widest text-muted-foreground">Your response landed in the</div>
              <div className="font-heading text-2xl font-bold" style={{ color: meta.color }}>{meta.label}</div>
            </div>
          )}
          <div className="rounded-md border border-border p-6">
            <div className="mb-2 text-xs uppercase tracking-widest text-muted-foreground">Reflect</div>
            <p className="text-base leading-relaxed">{feedback.reflection_prompt}</p>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="scenario-feedback-continue"
              className="flex-1"
              style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
              onClick={() => nav("/app")}
            >
              {feedback.module_complete ? "Finish — view certificate" : "Back to modules"}
            </Button>
            {!feedback.module_complete && (
              <Button
                variant="outline"
                data-testid="scenario-feedback-next"
                onClick={goNext}
              >
                Next scenario
              </Button>
            )}
          </div>
        </div>
      </MemberLayout>
    );
  }

  return (
    <MemberLayout>
      <div className="haruki-fade-in space-y-6">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          {scenario.incident_category.replace(/_/g, " ")} · {scenario.emotional_beat.replace(/_/g, " ")}
        </div>

        {scenario.image_url && (
          <img
            src={scenario.image_url.startsWith("http") ? scenario.image_url : `${process.env.REACT_APP_BACKEND_URL}${scenario.image_url}`}
            alt=""
            className="aspect-video w-full rounded-md border border-border object-cover"
          />
        )}

        <Card>
          <CardContent className="p-6">
            <p data-testid="scenario-text" className="text-base leading-relaxed">{scenario.scenario_text}</p>
            <div className="mt-4 flex flex-wrap gap-3 text-xs text-muted-foreground">
              <span>Setting: {scenario.setting.replace(/_/g, " ")}</span>
              <span>·</span>
              <span>Avatar: {scenario.avatar_pairing.replace(/_/g, " ")}</span>
              <span>·</span>
              <span>Modality: {scenario.modality_primary.replace(/_/g, " ")}</span>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-2">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Choose a response</div>
          {scenario.response_options.map((opt, i) => (
            <button
              key={opt.id}
              data-testid={`scenario-option-${i}`}
              onClick={() => submitChoice(opt)}
              disabled={submitting}
              className="block w-full rounded-md border border-border bg-card px-4 py-3 text-left text-sm transition-opacity hover:opacity-80 disabled:opacity-50"
            >
              <span className="text-foreground">{opt.text}</span>
              <span className="ml-2 text-xs text-muted-foreground">— {opt.modality?.replace(/_/g, " ")}</span>
            </button>
          ))}
        </div>

        <Card>
          <CardContent className="space-y-3 p-6">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">…or respond in your own words</div>
            <Textarea
              data-testid="scenario-voice-textarea"
              value={voiceText}
              onChange={(e) => setVoiceText(e.target.value)}
              placeholder="Type or use the mic to speak your response."
              className="min-h-[100px]"
            />
            <div className="flex items-center gap-2">
              {recording ? (
                <Button data-testid="voice-stop-button" onClick={stopRec} variant="outline">
                  <MicOff className="mr-2 h-4 w-4" /> Stop recording
                </Button>
              ) : (
                <Button data-testid="voice-record-button" onClick={startRec} variant="outline">
                  <Mic className="mr-2 h-4 w-4" /> Voice
                </Button>
              )}
              <Button
                data-testid="voice-submit-button"
                onClick={submitVoice}
                disabled={submitting}
                style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
              >
                <Send className="mr-2 h-4 w-4" />
                {submitting ? "Submitting…" : "Submit response"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Audio is transcribed and discarded — never stored.</p>
          </CardContent>
        </Card>
      </div>
    </MemberLayout>
  );
}
